'use client'

import { Tag } from '@/components/tag'
import { Badge } from '@/components/ui/badge'

import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTrigger,
} from '@/components/ui/drawer'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { AnimatePresence, motion } from 'framer-motion'

import { SheetTitle } from '@/components/ui/sheet'
import { TAG_COLOR_TABLE, TAG_TOOLTIP_TABLE } from '@/constants/tag-table'
import { AppRoutes } from '@/routes'
import { Button } from '@/shared/ui/button'
import { cn } from '@/utils/cn'
import { PopoverClose } from '@radix-ui/react-popover'
import { X } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { HTMLAttributes, ReactNode, useState } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip'
import { useProductDetailInfo } from './use-product-detail-info'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog'
import { ProductTag } from '@/schemas/product/product-tag'
import { formatCurrency } from '@/shared/lib/utils/format-currency'
import { Product } from '@/schemas/product'
type ProductDetailInfoProps = {
  productId: string
}

// TODO: UI 분리필요
export const ProductDetailInfo = ({
  productId,
  className,
  ...props
}: ProductDetailInfoProps & HTMLAttributes<HTMLDivElement>) => {
  const { data: detailInfo, pending } = useProductDetailInfo(productId)

  if (pending) {
    return null
  }
  if (!detailInfo) {
    return null
  }

  const {
    options,
    tags,
    rating,
    highestPrice,
    currentPrice,
    ratingCount,
    diffRate,
    discountCards,
    title,
    deliveryImageUrl,
    eachPrice,
  } = detailInfo

  return (
    <div className={className} {...props}>
      <div className="flex w-full items-start justify-between">
        <TagList tags={tags} />
        <RatingInfo
          className="translate-y-1"
          rating={rating / 100}
          ratingCount={ratingCount}
        />
      </div>
      <div className="mb-4 mt-3 flex w-full justify-between gap-5">
        <h2 className="text-body-01-semi">{title}</h2>
        {options && options.length !== 0 && (
          <OptionsBottomSheet options={options} productId={productId}>
            <Button
              size={'sm'}
              variant={'outline'}
              className="rounded-[6px] border border-gray-3 p-4 text-body-02-semi text-gray-5"
            >
              옵션
              <Image
                src="/icons/option-chevron-down.svg"
                width={16}
                height={16}
                className="ml-[2px] inline"
                alt="열기"
              />
            </Button>
          </OptionsBottomSheet>
        )}
      </div>
      <div className="mb-2 flex items-center gap-[6px]">
        <span className={'text-body-02-semi text-gray-5'}>
          최고가 {formatCurrency(highestPrice)}
        </span>
        {deliveryImageUrl && (
          <Image width={67} height={18} src={deliveryImageUrl} alt="" />
        )}
      </div>
      <h3 className="sr-only">{currentPrice.toLocaleString()}원</h3>
      <div className={cn('mb-4 flex gap-1 text-title-01')}>
        <span className="text-point-red">{diffRate}%</span>
        <span className="text-[#171717]">{formatCurrency(currentPrice)}</span>
      </div>
      <div className="flex justify-between">
        <h4>
          {eachPrice !== 0 && (
            <Tag variant="productDetail">1개당 {formatCurrency(eachPrice)}</Tag>
          )}
        </h4>
        <DiscountCards discountCards={discountCards} />
      </div>
    </div>
  )
}

const TagList = ({ tags }: { tags: ProductTag[] }) => {
  return (
    <div className="flex flex-wrap gap-1">
      {tags
        .filter(({ isVisible }) => isVisible)
        .map((tag) => {
          const renderTag = () => (
            <Badge
              size="large"
              key={tag.code}
              variant={TAG_COLOR_TABLE[tag.code]}
            >
              <span className="text-caption-01-semi">{tag.name}</span>
            </Badge>
          )
          const tooltip = TAG_TOOLTIP_TABLE[tag.code]

          if (!tooltip) {
            return <span key={tag.code}>{renderTag()}</span>
          }

          return (
            <TooltipProvider key={tag.code}>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>{renderTag()}</TooltipTrigger>
                <TooltipContent>{TAG_TOOLTIP_TABLE[tag.code]}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        })}
      {tags.length > 0 && (
        <Popover>
          <PopoverTrigger>
            <Image
              src="/icons/info.svg"
              alt=""
              width={14}
              height={14}
              className="inline -translate-y-px"
            />
          </PopoverTrigger>
          <PopoverContent className="bg-gray-1">
            <div>
              <div className="flex items-center justify-between">
                <h2 className="text-body-02-bold text-gray-8">태그 설명</h2>
                <PopoverClose className="translate-y-[-2px]">
                  <X className="size-4 text-gray-6" />
                </PopoverClose>
              </div>

              <ol className="mt-3 list-inside list-decimal text-caption-01-medium text-gray-6">
                <li>
                  역대 최저 : 똑소가 추적하고 있는 기간 중,{' '}
                  <strong className="text-gray-8">
                    현재 가장 저렴한 상품이에요.
                  </strong>
                </li>
                <li>
                  이번달 최저 : 최근{' '}
                  <strong className="text-gray-8">한 달</strong> 중, 현재{' '}
                  <strong className="text-gray-8">가장 저렴한 상품</strong>
                  이에요.
                </li>
                <li>
                  시선집중 : 유저들이{' '}
                  <strong className="text-gray-8">많이 찾는 상품</strong>
                  이에요.
                </li>
                <li>
                  똑소픽 : 유저들의{' '}
                  <strong className="text-gray-8">리뷰 반응이 좋은 상품</strong>
                  이에요.
                </li>
              </ol>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}

const RatingInfo = ({
  rating,
  ratingCount,
  className,
}: {
  rating: number
  ratingCount: number
  className?: string
}) => {
  return (
    <div className={cn('flex items-center gap-[4px]', className)}>
      <Image src="/icons/rating-star.svg" alt="평점" width={16} height={16} />
      <span className="text-caption-01-semi text-black">{rating}</span>
      <span className="text-caption-01-semi text-gray-5">{`(${ratingCount.toLocaleString()})`}</span>
    </div>
  )
}

const DiscountCards = ({
  discountCards,
}: {
  discountCards: {
    id: string
    cardName: string
    title: string
  }[]
}) => {
  return (
    <>
      {!!discountCards.length && (
        <Dialog>
          <DialogTrigger>
            <span className="ml-auto flex items-center gap-[5px] text-caption-01-semi text-gray-7">
              카드할인
              <Image
                src="/icons/info.svg"
                alt=""
                width={14}
                height={14}
                className="inline"
              />
            </span>
          </DialogTrigger>
          <DialogContent>
            <DialogTitle>
              <VisuallyHidden.Root>DiscountCardsDialog</VisuallyHidden.Root>
            </DialogTitle>
            <DialogHeader>
              <h2 className="text-body-02-medium text-gray-8">카드할인</h2>
            </DialogHeader>
            <div className="flex flex-col gap-[8px]">
              {discountCards.map((card) => (
                <div key={card.id} className="flex items-center gap-[4px]">
                  <span className="text-caption-01-semi text-black">
                    [{card.cardName}]
                  </span>
                  <span className="text-caption-01-semi text-gray-5">
                    {card.title}
                  </span>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

type OptionsBottomSheetProps = {
  children: ReactNode
  productId: string
  options: Product[]
}

export const OptionsBottomSheet = ({
  children,
  productId,
  options,
}: OptionsBottomSheetProps) => {
  const [displayLength, setDisplayLength] = useState(
    Math.min(options.length, 3),
  )
  const showMoreButton = options.length > 3 && displayLength < options.length
  const displayedOptions = options.slice(0, displayLength)

  const onOpenChange = (open: boolean) => {
    if (!open) {
      setDisplayLength(Math.min(options.length, 3))
    }
  }

  // Stagger animation variants
  const listVariants = {
    visible: {
      transition: {
        staggerChildren: 0.1, // 각 자식 요소들이 순차적으로 나타나는 지연 시간 설정
      },
    },
  }

  const itemVariants = {
    visible: { opacity: 1, y: 0 },
    hidden: { opacity: 0, y: 20 }, // 아래에서 위로 올라오는 애니메이션 효과
  }

  return (
    <Drawer onOpenChange={onOpenChange}>
      <DrawerTrigger asChild>{children}</DrawerTrigger>
      <DrawerContent className="bg-white sm:container">
        <DrawerHeader>
          <SheetTitle className="text-center">더 많은 옵션 보기</SheetTitle>
        </DrawerHeader>
        <motion.div
          layout
          transition={{
            type: 'spring',
            damping: 20,
            stiffness: 150,
            mass: 0.5,
          }}
        >
          <motion.ul
            key={displayLength}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={listVariants}
            className="max-h-[80vh] overflow-y-auto p-5 scrollbar-hide md:p-0"
          >
            <AnimatePresence mode="popLayout">
              {displayedOptions.map((option, index) => {
                const isLast = index === displayedOptions.length - 1
                return (
                  <motion.li key={option.id} variants={itemVariants}>
                    <Link
                      href={AppRoutes.product(option.id)}
                      className={cn(
                        'flex justify-between',
                        'border-opacity-10 py-[16px]',
                        !isLast && 'border-b border-black',
                      )}
                    >
                      <div>
                        {productId === option.id && (
                          <span className="text-caption-01-medium text-point-blue">
                            현재 보고있는 상품
                          </span>
                        )}
                        {option.subTitle && (
                          <span className="block min-h-[16px] text-caption-01-regular text-black">
                            {option.subTitle}
                          </span>
                        )}

                        <div className="flex items-center">
                          <span className="mr-[7px] text-heading-02-bold text-point-red">
                            {option.price.diffRate}%
                          </span>
                          <span className="text-heading-02-bold text-black">
                            {option.price.price.toLocaleString()}원
                          </span>
                        </div>
                        {option.weightInfo && (
                          <Tag variant="productDetail" className="mt-[8px]">
                            {option.weightInfo}
                          </Tag>
                        )}
                      </div>
                      <div>
                        <Image
                          src={option.imageUrl}
                          alt=""
                          width={92}
                          height={92}
                          className="rounded-[6px]"
                          unoptimized
                        />
                      </div>
                    </Link>
                  </motion.li>
                )
              })}
            </AnimatePresence>
          </motion.ul>
        </motion.div>

        {showMoreButton && (
          <button
            className="w-full rounded-[8px] border border-gray-2 py-[11px] text-gray-7"
            onClick={() => {
              setDisplayLength(options.length)
            }}
          >
            옵션 더 보기
            <Image
              src="/icons/chevron-down.svg"
              alt=""
              width={20}
              height={20}
              className="ml-[7px] inline"
            />
          </button>
        )}
      </DrawerContent>
    </Drawer>
  )
}
